import { html, LitElement, PropertyValues } from "lit";
import { property } from "lit/decorators.js";
import Layer from "ol/layer/Layer";
import type {
    OlMapContextMenuEventDetail,
    OlMapContextMenuEventType,
    OlMapKeyboardEventDetail,
    OlMapKeyboardEventType,
    OlMapPointerEventDetail,
    OlMapPointerEventType,
} from "../ol-map-events";

export class OlLayerReadyEvent extends Event {
    public layer: Layer;

    constructor(layer: Layer) {
        super("ol-layer-ready", {
            bubbles: true,
            composed: true,
        });

        this.layer = layer;
    }
}

export class OlLayerRemovedEvent extends Event {
    public layer: Layer;

    constructor(layer: Layer) {
        super("ol-layer-removed", {
            bubbles: true,
            composed: true,
        });

        this.layer = layer;
    }
}

export abstract class OlLayer<TLayer extends Layer> extends LitElement {
    protected layer?: TLayer;

    @property({
        type: Boolean,
    })
    visible: boolean = true;

    @property({
        type: Number,
    })
    opacity: number = 1;

    @property({
        type: Number,
    })
    zIndex: number = 1;

    @property({
        type: Boolean,
    })
    attributionsCollapsible: boolean = true;

    @property({
        type: Number,
    })
    minZoom: number | undefined = undefined;

    @property({
        type: Number,
    })
    maxZoom: number | undefined = undefined;

    public getLayer(): TLayer | undefined {
        return this.layer;
    }

    public connectedCallback(): void {
        super.connectedCallback();

        if (!this.layer) {
            const layer = this.createLayer();
            this.layer = layer;
        
            this.dispatchEvent(new OlLayerReadyEvent(layer));
        }
    }

    public disconnectedCallback(): void {
        super.disconnectedCallback();

        const layer = this.layer;

        if (layer) {
            this.dispatchEvent(new OlLayerRemovedEvent(layer));
            layer.dispose();
        }

        this.layer = undefined;
    }

    public getPointerEventTypes(): readonly OlMapPointerEventType[] {
        return [];
    }

    public getKeyboardEventTypes(): readonly OlMapKeyboardEventType[] {
        return [];
    }

    public getContextMenuEventTypes(): readonly OlMapContextMenuEventType[] {
        return [];
    }

    public handlePointerEvent(_event: OlMapPointerEventDetail): void {
    }

    public handleKeyboardEvent(_event: OlMapKeyboardEventDetail): void {
    }

    public handleContextMenuEvent(_event: OlMapContextMenuEventDetail): void {
    }

    protected update(changedProperties: PropertyValues): void {
        super.update(changedProperties);

        const layer = this.layer;
        if (!layer) {
            return;
        }

        if (changedProperties.has("visible")) {
            layer.setVisible(this.visible);
        }

        if (changedProperties.has("opacity")) {
            layer.setOpacity(this.opacity);
        }

        if (changedProperties.has("zIndex")) {
            layer.setZIndex(this.zIndex);
        }

        if (changedProperties.has("minZoom") && this.minZoom) {
            layer.setMinZoom(this.minZoom);
        }

        if (changedProperties.has("maxZoom") && this.maxZoom) {
            layer.setMaxZoom(this.maxZoom);
        }
    }

    protected render() {
        return undefined;
    }

    protected abstract createLayer(): TLayer;

}
